import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'

import { fetchProducts } from '@/lib/api/products'
import { fetchServiceCategories } from '@/lib/api/serviceCategories'
import { fetchServices } from '@/lib/api/services'
import {
  COMMISSION_CATEGORIES,
  COMMISSION_RATE_TYPES,
  COMMISSION_RULE_TYPES,
  commissionRuleTypeLabel,
  emptyCommissionRule,
} from '@/lib/masters/commissionProfileMaster'
import { cn } from '@/lib/utils'

function getFieldError(error, field) {
  return error?.response?.data?.errors?.[field]?.[0] ?? null
}

/**
 * @param {{
 *   form: ReturnType<import('@/lib/masters/commissionProfileMaster').emptyCommissionProfileForm>
 *   onChange: (next: ReturnType<import('@/lib/masters/commissionProfileMaster').emptyCommissionProfileForm>) => void
 *   error?: unknown
 * }} props
 */
export function CommissionRuleBuilder({ form, onChange, error }) {
  const [categories, setCategories] = useState([])
  const [services, setServices] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    setLoading(true)

    Promise.all([fetchServiceCategories(), fetchServices(), fetchProducts()])
      .then(([categoryItems, serviceItems, productItems]) => {
        if (!active) return
        setCategories(categoryItems)
        setServices(serviceItems.filter((item) => item.is_active))
        setProducts(productItems.filter((item) => item.is_active))
      })
      .catch(() => {
        if (!active) return
        setCategories([])
        setServices([])
        setProducts([])
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [])

  function updateRules(nextRules) {
    onChange({ ...form, rules: nextRules })
  }

  function updateRule(index, patch) {
    const nextRules = form.rules.map((rule, ruleIndex) =>
      ruleIndex === index ? { ...rule, ...patch } : rule,
    )
    updateRules(nextRules)
  }

  function addRule() {
    updateRules([
      ...form.rules,
      {
        ...emptyCommissionRule('commission_category'),
        sort: (form.rules.length + 1) * 10,
      },
    ])
  }

  function removeRule(index) {
    const rule = form.rules[index]
    if (rule.rule_type === 'default') return
    updateRules(form.rules.filter((_, ruleIndex) => ruleIndex !== index))
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Commission rules</h3>
          <p className="text-xs text-muted-foreground">
            One default rule is required. Add overrides for categories, services, or products.
          </p>
        </div>
        <button
          type="button"
          onClick={addRule}
          className="inline-flex items-center gap-1 rounded-xl border border-border/80 px-3 py-1.5 text-xs font-medium hover:bg-muted/80"
        >
          <Plus className="h-3.5 w-3.5" />
          Add override
        </button>
      </div>

      {getFieldError(error, 'rules') ? (
        <p className="text-sm text-destructive" role="alert">
          {getFieldError(error, 'rules')}
        </p>
      ) : null}

      <div className="space-y-3">
        {form.rules.map((rule, index) => (
          <div key={`${rule.rule_type}-${index}`} className="rounded-2xl border border-border/70 p-4">
            <div className="mb-3 flex items-start justify-between gap-3">
              <p className="text-sm font-medium">{commissionRuleTypeLabel(rule.rule_type)}</p>
              {rule.rule_type !== 'default' ? (
                <button
                  type="button"
                  onClick={() => removeRule(index)}
                  className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                  aria-label="Remove rule"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {rule.rule_type !== 'default' ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Rule type
                  </label>
                  <select
                    value={rule.rule_type}
                    onChange={(e) => updateRule(index, { rule_type: e.target.value })}
                    className={cn(
                      'h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm',
                      'focus:outline-none focus:ring-2 focus:ring-ring',
                    )}
                  >
                    {COMMISSION_RULE_TYPES.filter((type) => type !== 'default').map((type) => (
                      <option key={type} value={type}>
                        {commissionRuleTypeLabel(type)}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {rule.rule_type === 'commission_category' ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Category
                  </label>
                  <select
                    value={rule.commission_category}
                    onChange={(e) => updateRule(index, { commission_category: e.target.value })}
                    className="h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm"
                  >
                    {COMMISSION_CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {rule.rule_type === 'service_category' ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Service category
                  </label>
                  <select
                    value={rule.service_category_id}
                    onChange={(e) => updateRule(index, { service_category_id: e.target.value })}
                    disabled={loading}
                    className="h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm"
                  >
                    <option value="">Select category</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {rule.rule_type === 'service' ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Service
                  </label>
                  <select
                    value={rule.service_id}
                    onChange={(e) => updateRule(index, { service_id: e.target.value })}
                    disabled={loading}
                    className="h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm"
                  >
                    <option value="">Select service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {rule.rule_type === 'product' ? (
                <div>
                  <label className="mb-1 block text-xs font-medium text-muted-foreground">
                    Product
                  </label>
                  <select
                    value={rule.product_id}
                    onChange={(e) => updateRule(index, { product_id: e.target.value })}
                    disabled={loading}
                    className="h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm"
                  >
                    <option value="">Select product</option>
                    {products.map((product) => (
                      <option key={product.id} value={product.id}>
                        {product.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Rate type
                </label>
                <select
                  value={rule.rate_type}
                  onChange={(e) => updateRule(index, { rate_type: e.target.value })}
                  className="h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm"
                >
                  {COMMISSION_RATE_TYPES.map((rateType) => (
                    <option key={rateType} value={rateType}>
                      {rateType === 'percent' ? 'Percent of net' : 'Flat per unit'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-muted-foreground">
                  Rate value
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={rule.rate_value}
                  onChange={(e) => updateRule(index, { rate_value: e.target.value })}
                  className="h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
